import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { asapScheduler, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppModeService } from './services/app-mode.service';
import { LoaderService } from './services/loader.service';
import { UiService } from './services/ui.service';
import { IntercomService } from './services/intercom.service';
import { FeatureFlagService } from './services/featureflag.service';
import { AuthChangeEvent, AuthenticationService } from './services/authentication.service';
import { AppStateService } from './services/app-state.service';
import { CrossTabLogoutService } from './core/services/cross-tab-logout.service';
import { User } from './core/models';
import { NeoBaseComponent } from './shared/components/base/neo-base.component';
import { getNeoLoggingEnabled } from './core/settings/app.config';
import { NeoTrackEventStatus, NeoTrackEventAction, NeoTrackEventComponent, NeoTrackEventLocation } from './services/amplitude.service.models';
import { OtelService } from './services/otel.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent extends NeoBaseComponent implements OnInit, OnDestroy {
  public title = 'NeoOne';
  public isIframe = false;
  public _loader$!: boolean;
  private previousPath: string = '';

  // Single cross-tab coordination in the app: react to a peer logout by
  // showing a full-screen "you've been signed out" overlay. The user clicks
  // the overlay button to reload — this serialises peer-tab reauth so N
  // simultaneous loginRedirects don't collide on MSAL's shared-localStorage
  // interaction state.
  private readonly logoutService = inject(CrossTabLogoutService);

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthenticationService,
    private loaderService: LoaderService,
    private appModeService: AppModeService,
    private uiService: UiService,
    private intercomService: IntercomService,
    private featureFlagService: FeatureFlagService,
    private otelService: OtelService,
    public appStateService: AppStateService
  ) {
    super();
    this.logInitialisation();
    this.neoLogService.log('AppComponent->constructor');
    this.registerSubscriptions();
    this.serviceInitialisations();
  }

  public ngOnInit(): void {
    this.neoLogService.log('AppComponent->ngOnInit');
    this.isIframe = window !== window.parent && !window.opener;
  }

  private logInitialisation(): void {
    this.activatedRoute.queryParamMap.subscribe(params => {
      // Check for clear-storage parameter
      const clearStorage = params.get('clear-storage');
      if (clearStorage === 'true') {
        this.neoLogService.log('🧹 Clearing all browser storage...');
        localStorage.clear();
        sessionStorage.clear();
        this.neoLogService.log('✅ Storage cleared! Reloading without parameter...');
        // Reload without the clear-storage parameter
        window.location.href = window.location.origin + window.location.pathname;
        return;
      }

      const consoleLogging = params.get('neo-logging');
      this.neoLogService.isLoggingEnabled = (consoleLogging === 'true');
      if (this.neoLogService.isLoggingEnabled) {
        this.neoLogService.log('Console Logging Enabled by query param:', this.neoLogService.isLoggingEnabled);
      } else {
        // check the environment variable
        this.neoLogService.isLoggingEnabled = getNeoLoggingEnabled();
        if (this.neoLogService.isLoggingEnabled) {
          this.neoLogService.log('Console Logging Enabled by environment varaible:', this.neoLogService.isLoggingEnabled);
        } else {
          this.neoLogService.log('Console Logging Enabled:', this.neoLogService.isLoggingEnabled);
        }
      }
      this.neoLogService.showDebug = (params.get('neo-debug') === 'true');
    });
  }

  private serviceInitialisations(): void {
    this.amplitudeService.initialise();
    this.otelService.initialise();
    this.otelService.instrumentRouter(this.router);
    this.uiService.registerSvgIcons();
    this.appModeService.initAppModeTracking();
    this.registerLoader();
    void this.authService.initialize();
  }

  private registerSubscriptions(): void {
    this.neoLogService.log('AppComponent->registerSubscriptions');
    this.subscribeAuthEvents();
    this.subscribeAppStateEvents();
    this.subscribeCrossTabLogout();
  }

  /**
   * On peer-tab logout, mark the AppState flag and let the template render a
   * full-screen panel. The user clicks "Sign in again" to reload — see
   * {@link reloadForLogout}. This deliberately avoids auto-reload so peer
   * tabs don't fan out into simultaneous loginRedirects.
   * @private
   */
  private subscribeCrossTabLogout(): void {
    this.logoutService.logout$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.neoLogService.log('AppComponent->cross-tab logout observed, surfacing overlay');
        this.appStateService.markLoggedOutElsewhere();
      });
  }

  /**
   * Click handler for the cross-tab logout overlay. A simple reload is
   * sufficient — MSAL's shared localStorage was wiped by the signalling
   * tab, so the boot path lands at the login flow naturally.
   */
  public reloadForLogout(): void {
    this.neoLogService.log('AppComponent->reloadForLogout');
    window.location.reload();
  }

  private subscribeAuthEvents(): void {
    this.authService.changedEvent$
    .pipe(takeUntil(this.destroy$))
    .subscribe((authChangeEvent: AuthChangeEvent) => {
      this.neoLogService.log('AppComponent->registerSubscriptions->authChangeEvent:', authChangeEvent);
      if(authChangeEvent.user) {
        this.trackUser(authChangeEvent.user);
      } else {
        // Otherwise spans emitted after logout (e.g. the login page itself) would stay
        // tagged with the previous session's identity.
        this.otelService.setCurrentUserId(undefined);
        this.otelService.setCurrentTenantKey(undefined);
        this.otelService.setCurrentStaffId(undefined);
      }
    });
  }

  /**
   * Subscribe to app state changes
   * When app is fully ready, enable Intercom
   */
  private subscribeAppStateEvents(): void {
    this.appStateService.ready$
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {
      this.neoLogService.log('AppComponent->App ready, enabling Intercom');
      this.intercomService.checkAndEnableIntercom();
    });
  }

  public login(): void {
    this.amplitudeService.track(NeoTrackEventAction.View, NeoTrackEventComponent.App, NeoTrackEventLocation.Login, NeoTrackEventStatus.None);
    this.authService.login();
  }

  public logout(): void {
    this.amplitudeService.track(NeoTrackEventAction.View, NeoTrackEventComponent.App, NeoTrackEventLocation.Logout, NeoTrackEventStatus.None);
    // NEO-3626: a user clicking Logout is a deliberate fresh start, so release the
    // library-redirect breaker. AuthenticationService.logout() deliberately does not
    // do this itself — it is also reached from automatic error handling, where a
    // failing loop would otherwise reset its own breaker on every lap.
    this.authService.clearLibraryRedirectBreaker();
    this.authService.logout();
  }

  public editProfile(): void {
    this.authService.loginForEditProfile();
  }

  private trackUser(user: User): void {
    const featureFlags = this.featureFlagService.getOrganizationFeatureFlags();
    this.amplitudeService.setActiveUser(user, featureFlags);
    this.otelService.setCurrentUserId(user.b2cObjectId);
    this.otelService.setCurrentStaffId(user.staffId ?? user.userId);
  }

  private registerLoader(): void {
    this.loaderService._loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loaderValue: boolean) => {
        asapScheduler.schedule(() => {
          this._loader$ = loaderValue;
        });
      });
  }

  public ngOnDestroy(): void {
    this.logoutService.dispose();
    super.ngOnDestroy();
  }
}
